import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles((theme) => ({
  panel: {
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.shadows[1],
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9fafb',
    },
    transition: 'background-color 0.2s ease',
  },
  content: {
    padding: theme.spacing(2),
    paddingTop: 0,
  },
  section: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  chip: {
    margin: 0,
    height: '28px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  clearButton: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.875rem",
  },
}));

const FilterPanel = ({ 
  open, 
  onToggle, 
  selectedQueueIds, 
  selectedTagIds,
  queues,
  tags,
  onClearFilters,
  onRemoveQueue,
  onRemoveTag
}) => {
  const classes = useStyles();
  const theme = useTheme();
  
  const activeFiltersCount = (selectedQueueIds?.length || 0) + (selectedTagIds?.length || 0);
  
  return (
    <Paper className={classes.panel} elevation={0}>
      <Box 
        className={classes.header}
        onClick={onToggle}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FilterListIcon fontSize="small" style={{ color: theme.palette.text.secondary }} />
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Filtros
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip 
              label={activeFiltersCount} 
              size="small" 
              color="primary"
              style={{ height: '20px', fontSize: '0.7rem', minWidth: '24px' }}
            />
          )}
        </Box>
        <Typography variant="caption" style={{ color: theme.palette.text.secondary }}>
          {open ? 'Ocultar' : 'Mostrar'}
        </Typography>
      </Box>
      
      <Collapse in={open}>
        <Box className={classes.content}>
          {/* Sección Departamentos */}
          {(selectedQueueIds?.length > 0) && (
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <span role="img" aria-label="departamentos">📁</span> Departamentos ({selectedQueueIds.length})
              </Typography>
              <Box className={classes.chipsContainer}>
                {selectedQueueIds.map(queueId => {
                  const queue = queues?.find(q => q.id === queueId);
                  if (!queue) return null;
                  return (
                    <Chip
                      key={queueId}
                      label={queue.name}
                      onDelete={() => onRemoveQueue(queueId)}
                      className={classes.chip}
                      style={{ 
                        backgroundColor: queue.color || theme.palette.primary.main, 
                        color: '#fff' 
                      }}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Sección Etiquetas */}
          {(selectedTagIds?.length > 0) && (
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <span role="img" aria-label="etiquetas">🏷️</span> Etiquetas ({selectedTagIds.length})
              </Typography>
              <Box className={classes.chipsContainer}>
                {selectedTagIds.map(tagId => {
                  const tag = tags?.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <Chip
                      key={tagId}
                      label={tag.name}
                      onDelete={() => onRemoveTag(tagId)}
                      className={classes.chip}
                      style={{ 
                        backgroundColor: tag.color || theme.palette.primary.main, 
                        color: '#fff' 
                      }}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Botón Limpiar */}
          {activeFiltersCount > 0 && (
            <Box className={classes.clearButton}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearFilters();
                }}
                style={{ color: theme.palette.error.main }}
              >
                <ClearIcon fontSize="small" />
                <Typography variant="caption" style={{ marginLeft: 4 }}>
                  Limpiar Filtros
                </Typography>
              </IconButton>
            </Box>
          )}
          
          {/* Mensaje cuando no hay filtros */}
          {activeFiltersCount === 0 && (
            <Box className={classes.emptyState}>
              <Typography variant="body2">
                No hay filtros activos. Selecciona departamentos o etiquetas arriba.
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default FilterPanel;
